//  src/hooks/useVehicleTracker.js

import { useState, useEffect, useRef } from 'react';
import { vehicleTrackerService } from '../services/vehicleTrackerService';
import { useAuth } from '../contexts/AuthContext';
import { getCurrentLocation, API, APP_CONSTANTS } from '../utils/config';
import spatialOptimizer from '../utils/spatialOptimizer';

import { pushNotificationService } from '../services/pushNotificationService';


// Service Worker message handler function - moved inside hook to access state setters


// 1. Wake Lock API (keeps screen awake during notifications)
const requestWakeLock = async () => {
  if ('wakeLock' in navigator) {
    try {
      const wakeLock = await navigator.wakeLock.request('screen');
      //
      return wakeLock;
    } catch (err) {
    }
  }
};

// 2. Enhanced vibration patterns for different urgency levels
const vibrationPatterns = {
  low: [200],
  medium: [300, 100, 300],
  high: [500, 200, 500, 200, 500],
  urgent: [1000, 300, 1000, 300, 1000]
};

// 3. Sound volume based on distance
const calculateSoundVolume = (distanceMiles) => {
  if (distanceMiles < 1) return 1.0;      // Very close - max volume
  if (distanceMiles < 3) return 0.8;      // Close - high volume
  if (distanceMiles < 5) return 0.6;      // Medium distance - medium volume
  return 0.4;                             // Far - lower volume
};

// 4. Smart notification timing (don't spam user)
const getNotificationUrgency = (distanceMiles, estimatedArrival) => {
  if (distanceMiles < 0.5 || estimatedArrival < 2) return 'urgent';
  if (distanceMiles < 2 || estimatedArrival < 5) return 'high';
  if (distanceMiles < 5 || estimatedArrival < 10) return 'medium';
  return 'low';
};

// 5. Battery-aware notifications
const isBatteryLow = async () => {
  if ('getBattery' in navigator) {
    try {
      const battery = await navigator.getBattery();
      return battery.level < 0.2; // Less than 20%
    } catch (error) {
      return false;
    }
  }
  return false;
};

// 6. Connection-aware notifications
const isSlowConnection = () => {
  if ('connection' in navigator) {
    const connection = navigator.connection;
    return connection.effectiveType === 'slow-2g' || connection.effectiveType === '2g';
  }
  return false;
};

/**
 * Calculate distance between two coordinates using Haversine formula
 * @param {Object} coord1 - First coordinate {lat, lng}
 * @param {Object} coord2 - Second coordinate {lat, lng}
 * @returns {number} Distance in miles
 */
const calculateDistance = (coord1, coord2) => {
  const R = 3959; // Earth's radius in miles
  const dLat = (coord2.lat - coord1.lat) * Math.PI / 180;
  const dLng = (coord2.lng - coord1.lng) * Math.PI / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(coord1.lat * Math.PI / 180) * Math.cos(coord2.lat * Math.PI / 180) *
    Math.sin(dLng / 2) * Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};

/**
 * Check if current time is within subscription time windows
 * @param {Array} timeWindows - Array of time window objects
 * @param {Object} daysOfWeek - Days of week object
 * @returns {boolean} True if current time is within window
 */
const isWithinTimeWindow = (timeWindows, daysOfWeek) => {
  const now = new Date();
  const currentDay = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'][now.getDay()];
  const currentTime = now.getHours() * 60 + now.getMinutes(); // minutes since midnight

  // Check if today is enabled
  if (!daysOfWeek[currentDay]) {
    return false;
  }

  // Check if current time is within any enabled time window
  return timeWindows.some(window => {
    if (!window.is_enabled) return false;

    const [startHour, startMin] = window.start_time.split(':').map(Number);
    const [endHour, endMin] = window.end_time.split(':').map(Number);
    const startTime = startHour * 60 + startMin;
    const endTime = endHour * 60 + endMin;

    return currentTime >= startTime && currentTime <= endTime;
  });
};

/**
 * Get town sequence number by ID
 * @param {string} townId - Town ID to lookup
 * @param {Array} towns - Array of town objects
 * @returns {number} Sequence number or -1 if not found
 */
const getTownSequence = (townId, towns) => {
  const town = towns.find(t => t.id === townId);
  return town ? town.sequence_number : -1;
};

// Distance history tracking for passed pickup detection
const busDistanceHistory = new Map();
// Track which buses have been marked as passed for each subscription
const passedBusesPerSubscription = new Map();

/**
 * Check if bus has passed a pickup point based on distance pattern and sequence numbers
 * @param {string} busId - Bus ID for tracking distance history
 * @param {string} subscriptionId - Subscription ID for tracking passed state per subscription
 * @param {Object} busLocation - Bus coordinates {lat, lng}
 * @param {Object} pickupLocation - Pickup coordinates {lat, lng} 
 * @param {number} originSequence - Origin town sequence number
 * @param {number} destinationSequence - Destination town sequence number
 * @param {Array} towns - Array of town objects
 * @returns {boolean} True if bus has passed the pickup point
 */
const hasBusPassedPickup = (busId, subscriptionId, busLocation, pickupLocation, originSequence, destinationSequence, towns) => {
  const subscriptionKey = `${subscriptionId}_${busId}`;

  // Check if this bus has already been marked as passed for this subscription
  if (passedBusesPerSubscription.has(subscriptionKey)) {
    const passedData = passedBusesPerSubscription.get(subscriptionKey);
    const currentDistance = calculateDistance(busLocation, pickupLocation);

    // Reset the "passed" status if the bus is now very close again (< 0.5 miles)
    // This handles cases where the bus completes a route cycle and approaches again
    if (currentDistance < 0.5) {
      //
      passedBusesPerSubscription.delete(subscriptionKey);
    } else {
      // Bus is still marked as passed
      return true;
    }
  }
  const currentDistance = calculateDistance(busLocation, pickupLocation);

  // Get or initialize distance history for this bus
  if (!busDistanceHistory.has(busId)) {
    busDistanceHistory.set(busId, []);
  }

  const history = busDistanceHistory.get(busId);

  // Add current distance to history (keep last 5 measurements)
  history.push({
    distance: currentDistance,
    timestamp: Date.now()
  });

  // Keep only recent measurements (last 5 or last 10 minutes)
  const tenMinutesAgo = Date.now() - (10 * 60 * 1000);
  const filteredHistory = history
    .filter(entry => entry.timestamp > tenMinutesAgo)
    .slice(-5);

  busDistanceHistory.set(busId, filteredHistory);

  //

  // Distance-based detection: check if distance was very close (< 0.2 miles) and is now increasing
  if (filteredHistory.length >= 3) {
    const recentDistances = filteredHistory.slice(-3).map(entry => entry.distance);
    const minRecentDistance = Math.min(...recentDistances);
    const latestDistance = recentDistances[recentDistances.length - 1];

    // If the bus was very close (within 0.2 miles) and distance is now increasing significantly
    if (minRecentDistance <= 0.2 && latestDistance > minRecentDistance + 0.1) {
      //

      // Mark this bus as passed for this subscription
      passedBusesPerSubscription.set(subscriptionKey, {
        timestamp: Date.now(),
        minDistance: minRecentDistance,
        currentDistance: latestDistance
      });

      return true;
    }
  }

  // Fallback to sequence-based detection for additional validation
  // Find the closest town to the bus current location
  let closestTown = null;
  let minDistance = Infinity;

  towns.forEach(town => {
    const townLocation = { lat: town.coordinates.lat, lng: town.coordinates.lng };
    const distance = calculateDistance(busLocation, townLocation);
    if (distance < minDistance) {
      minDistance = distance;
      closestTown = town;
    }
  });

  if (!closestTown) return false;

  // Assume buses travel in sequence order
  const busSequence = closestTown.sequence_number;

  // Find closest town to pickup location to estimate pickup sequence
  let closestPickupTown = null;
  let minPickupDistance = Infinity;

  towns.forEach(town => {
    const townLocation = { lat: town.coordinates.lat, lng: town.coordinates.lng };
    const distance = calculateDistance(pickupLocation, townLocation);
    if (distance < minPickupDistance) {
      minPickupDistance = distance;
      closestPickupTown = town;
    }
  });

  if (!closestPickupTown) return false;

  const pickupSequence = closestPickupTown.sequence_number;

  // Bus has passed pickup if it's further along the route than the pickup point
  // Handle both forward and reverse direction routes
  if (originSequence < destinationSequence) {
    // Forward direction (increasing sequence)
    return busSequence > pickupSequence;
  } else {
    // Reverse direction (decreasing sequence)  
    return busSequence < pickupSequence;
  }
};

/**
 * Get subscription display name (custom message or route description)
 * @param {Object} subscription - Enhanced subscription object
 * @param {Array} towns - Array of town objects  
 * @returns {string} Display name for subscription
 */
const getSubscriptionDisplayName = (subscription, towns) => {
  // Use custom message if available
  if (subscription.notification_preferences?.custom_message) {
    return subscription.notification_preferences.custom_message;
  }

  // Fall back to route description
  const originTown = towns.find(t => t.id === subscription.origin_town_id)?.name || subscription.origin_town_id;
  const destinationTown = towns.find(t => t.id === subscription.destination_town_id)?.name || subscription.destination_town_id;

  return `${originTown} → ${destinationTown}`;
};

/**
 * OPTIMIZED: Filter buses for subscribed routes within distance limit
 * Uses spatial indexing and caching for high performance
 * @param {Array} buses - Array of bus objects  
 * @param {Array} enhancedSubscriptions - Array of enhanced subscription objects
 * @param {Array} towns - Array of town objects
 * @returns {Array} Filtered buses with distance and time estimates
 */
const filterSubscribedBuses = (buses, enhancedSubscriptions, towns) => {
  // Use high-performance spatial optimizer for large datasets
  if (enhancedSubscriptions.length > 50 || buses.length > 20) {
    //
    return spatialOptimizer.optimizedFilterSubscribedBuses(buses, enhancedSubscriptions, towns);
  }

  // Fallback to original algorithm for smaller datasets
  //
  return filterSubscribedBusesOriginal(buses, enhancedSubscriptions, towns);
};

/**
 * Original filtering algorithm (kept as fallback for small datasets)
 */
const filterSubscribedBusesOriginal = (buses, enhancedSubscriptions, towns) => {
  const subscribedBuses = [];

  // Only process active subscriptions within current time window
  const activeSubscriptions = enhancedSubscriptions.filter(sub =>
    sub.is_active && isWithinTimeWindow(sub.time_windows, sub.days_of_week)
  );

  // For each bus, find all relevant subscriptions it could serve
  buses.forEach(bus => {
    if (!bus.last_location || bus.vehicle_type !== 'bus') return;

    const busLocation = {
      lat: bus.last_location.latitude,
      lng: bus.last_location.longitude
    };

    // Find all subscriptions this bus could potentially serve
    const relevantSubscriptions = [];

    activeSubscriptions.forEach(subscription => {
      const originSequence = getTownSequence(subscription.origin_town_id, towns);
      const destinationSequence = getTownSequence(subscription.destination_town_id, towns);
      const pickupLocation = subscription.pickup_location;

      if (originSequence === -1 || destinationSequence === -1) {
        return;
      }

      const distanceToPickup = calculateDistance(busLocation, pickupLocation);

      // Check if bus is within 30 miles and hasn't passed the pickup point
      if (distanceToPickup <= 30 &&
        !hasBusPassedPickup(bus.id, subscription.id, busLocation, pickupLocation, originSequence, destinationSequence, towns)) {

        relevantSubscriptions.push({
          subscription,
          distance: distanceToPickup,
          timeEstimate: Math.round((distanceToPickup / 25) * 60) // 25 mph average
        });
      }
    });

    // If bus serves multiple subscriptions, show the nearest one
    if (relevantSubscriptions.length > 0) {
      // Sort by distance and take the nearest
      const nearest = relevantSubscriptions.sort((a, b) => a.distance - b.distance)[0];
      const subscription = nearest.subscription;

      subscribedBuses.push({
        ...bus,
        subscription: {
          id: subscription.id,
          origin_town: towns.find(t => t.id === subscription.origin_town_id)?.name || subscription.origin_town_id,
          destination_town: towns.find(t => t.id === subscription.destination_town_id)?.name || subscription.destination_town_id,
          pickup_location: subscription.pickup_location,
          departure_time: subscription.time_windows[0]?.start_time || 'N/A',
          display_name: getSubscriptionDisplayName(subscription, towns)
        },
        distance: nearest.distance.toFixed(1),
        timeEstimate: Math.max(1, nearest.timeEstimate) // At least 1 minute
      });
    }
  });

  // Sort by distance (nearest first)
  return subscribedBuses.sort((a, b) => parseFloat(a.distance) - parseFloat(b.distance));
};

/**
 * Custom hook for Vehicle Tracker dashboard state management
 * Handles all data fetching, WebSocket connections, and state updates
 */
export const useVehicleTracker = () => {
  // Authentication
  const { user } = useAuth();

  // Navigation state - persist active tab in localStorage
  const [activeTab, setActiveTab] = useState(() => {
    // Try to restore the last active tab from localStorage
    try {
      const savedTab = localStorage.getItem('activeTab');
      return savedTab || 'map';
    } catch (error) {
      return 'map';
    }
  });

  // Vehicle data - always start fresh, API will populate
  const [publicVehicles, setPublicVehicles] = useState([]);
  const [userVehicles, setUserVehicles] = useState([]);
  const [sharedVehicles, setSharedVehicles] = useState([]);
  const [busRoutes, setBusRoutes] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [routeSubscriptions, setRouteSubscriptions] = useState([]);
  const [enhancedSubscriptions, setEnhancedSubscriptions] = useState([]);
  const [towns, setTowns] = useState([]);

  // Advertisement data
  const [ads, setAds] = useState([]);
  const [allPublicAds, setAllPublicAds] = useState([]);
  const [currentAd, setCurrentAd] = useState(null);
  const [showInterstitial, setShowInterstitial] = useState(false);
  const [currentBannerIndex, setCurrentBannerIndex] = useState(0);

  // Map filter state
  const [mapFilters, setMapFilters] = useState({
    showBuses: true,
    showVans: true,
    showPrivateVehicles: false,
    showUserVehicles: true
  });
  const [userLocation, setUserLocation] = useState(null);

  // UI state
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Map state - Default center is Belmopan, Belize
  const [selectedVehicleId, setSelectedVehicleId] = useState(null);
  const [mapCenter, setMapCenter] = useState(APP_CONSTANTS.MAP.BELIZE_CENTER);
  const [mapZoom, setMapZoom] = useState(APP_CONSTANTS.MAP.DEFAULT_ZOOM);
  const [subscribedBusesWithDistance, setSubscribedBusesWithDistance] = useState([]);

  // WebSocket reference
  const wsRef = useRef(null);

  // Auto-enable tabs based on user data
  const autoEnableTabsBasedOnData = (userVehicles, sharedVehicles) => {
    try {
      // Get current tab settings
      const savedSettings = localStorage.getItem('userTabSettings');
      let currentSettings = {
        map: true,
        'enhanced-subs': true,
        vehicles: false,
        shared: false,
        buses: false,
        subscriptions: false,
        ads: false
      };

      if (savedSettings) {
        try {
          currentSettings = { ...currentSettings, ...JSON.parse(savedSettings) };
        } catch (error) {
        }
      }

      let settingsChanged = false;

      // Auto-enable My Vehicles tab if user has registered vehicles
      if (userVehicles && userVehicles.length > 0 && !currentSettings.vehicles) {
        //
        currentSettings.vehicles = true;
        settingsChanged = true;
      }

      // Auto-enable Shared Vehicles tab if user has shared vehicles
      if (sharedVehicles && sharedVehicles.length > 0 && !currentSettings.shared) {
        //
        currentSettings.shared = true;
        settingsChanged = true;
      }

      // Save updated settings if changed
      if (settingsChanged) {
        localStorage.setItem('userTabSettings', JSON.stringify(currentSettings));

        // Dispatch event to notify components of the change
        window.dispatchEvent(new CustomEvent('tabSettingsUpdated', { detail: currentSettings }));

        //
      }
    } catch (error) {
    }
  };

  // Fetch all data - Simplified for garbage tracker
  // Only fetch what's needed: public vehicles (garbage trucks) and routes
  // Also fetch user vehicles immediately if logged in for faster My Trucks tab load
  const fetchAllData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Core data needed for garbage tracker
      // Include user vehicles if logged in for immediate access to My Trucks tab
      const [
        publicVehiclesData,
        busRoutesData,
        enhancedSubscriptionsData,
        userVehiclesData,
        sharedVehiclesData
      ] = await Promise.all([
        vehicleTrackerService.getPublicVehicles(),
        vehicleTrackerService.getBusRoutes().catch(() => []),
        vehicleTrackerService.getEnhancedSubscriptions().catch(() => []),
        // Fetch user vehicles immediately (will return [] if not logged in)
        vehicleTrackerService.getUserVehicles().catch(() => []),
        vehicleTrackerService.getSharedVehicles().catch(() => ({ shared_vehicles: [] }))
      ]);

      setPublicVehicles(publicVehiclesData);
      setBusRoutes(busRoutesData);
      setEnhancedSubscriptions(enhancedSubscriptionsData);
      setUserVehicles(userVehiclesData);
      setSharedVehicles(sharedVehiclesData.shared_vehicles || []);

      // Auto-enable tabs if user has vehicles
      if (userVehiclesData.length > 0 || (sharedVehiclesData.shared_vehicles?.length > 0)) {
        autoEnableTabsBasedOnData(userVehiclesData, sharedVehiclesData.shared_vehicles || []);
      }

      // Set empty arrays for unused features to prevent errors
      setSubscriptions([]);
      setRouteSubscriptions([]);
      setTowns([]);
      setAllPublicAds([]);
      setAds([]);

    } catch (error) {
      setError('Failed to load garbage truck data');
    } finally {
      setLoading(false);
    }
  };

  // Initialize geolocation-based map center
  // Note: For garbageTracker, we always start centered on Belmopan, Belize
  // User can click "Locate Me" to center on their location
  const initializeMapCenter = async () => {
    // Always use Belmopan as the default center for this app
    // Don't try to get user location automatically - let them click "Locate Me"
    setMapCenter(APP_CONSTANTS.MAP.BELIZE_CENTER);
  };

  // WebSocket connection management - SIMPLIFIED: Back to direct approach with cleanup
  const setupWebSocket = () => {
    if (!user?.id) return;

    // Clean up any existing connection first
    if (wsRef.current) {
      //
      wsRef.current.close();
      wsRef.current = null;
    }

    try {
      // WebSocket URL should be /ws/{user_id} (not /api/ws) - remove /api suffix
      const wsUrl = `${API.replace('https', 'wss').replace('http', 'ws').replace('/api', '')}/ws/${user.id}`;
      //

      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        //
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          //
          handleWebSocketMessage(data);
        } catch (error) {
          //
        }
      };

      ws.onclose = () => {
        //
        // Reconnect after 5 seconds if not intentionally closed
        if (wsRef.current === ws) {
          setTimeout(() => {
            if (user?.id) {
              //
              setupWebSocket();
            }
          }, 5000);
        }
      };

      ws.onerror = (error) => {
        //
      };

    } catch (error) {
    }
  };

  // Handle WebSocket messages
  const handleWebSocketMessage = (data) => {
    //

    switch (data.type) {
      case 'location_update':
        //
        handleLocationUpdate(data);
        break;
      case 'bus_approaching':
        //
        handleBusApproaching(data);
        break;
      case 'bus_notification':
        //
        handleBusNotification(data);
        break;
      default:
      //
    }
  };

  // Handle bus notification messages - CORRECTED VERSION (NO HOOKS)
  const handleBusNotification = async (data) => {
    //

    // Check if we should reduce notifications due to battery/connection
    const batteryLow = await isBatteryLow();
    const slowConnection = isSlowConnection();

    if (batteryLow && data.distance_miles > 2) {
      //
      return;
    }

    const urgency = getNotificationUrgency(data.distance_miles, data.estimated_arrival);
    const volume = calculateSoundVolume(data.distance_miles);

    // Add to notifications state
    setNotifications(prev => [...prev, {
      id: data.notification_id || Date.now(),
      type: 'bus_notification',
      urgency: urgency,
      title: 'Garbage Truck Approaching!',
      message: data.message,
      distance: data.distance_miles,
      estimatedArrival: data.estimated_arrival,
      busId: data.bus_id,
      timestamp: new Date()
    }]);

    // 1. VIBRATION (for mobile devices)
    if ('navigator' in window && 'vibrate' in navigator) {
      navigator.vibrate(vibrationPatterns[urgency]);
    }

    // 2. SOUND with distance-based volume (skip on slow connections)
    if (!slowConnection) {
      try {
        const audio = new Audio('/notification.mp3');
        audio.volume = volume;
        await audio.play();
      } catch (error) {
      }
    }

    // 3. Wake lock for urgent notifications
    if (urgency === 'urgent') {
      await requestWakeLock();
    }

    // 4. SYSTEM NOTIFICATION - Service Worker method
    if ('Notification' in window && Notification.permission === 'granted') {
      try {
        // Use Service Worker notification (supports actions and background)
        if ('serviceWorker' in navigator) {
          const registration = await navigator.serviceWorker.ready;
          await registration.showNotification('🚛 Garbage Truck Approaching!', {
            body: `${data.message}\n📍 ${data.distance_miles.toFixed(1)} miles away\n⏱️ ETA: ${data.estimated_arrival} min`,
            icon: '/favicon.ico',
            badge: '/favicon.ico',
            tag: data.notification_id,
            requireInteraction: urgency === 'urgent',
            vibrate: vibrationPatterns[urgency],
            silent: false,
            actions: [
              // { //this is the one that shows on the notification drawer.
              //   action: 'view',
              //   title: '📍 View on Map',
              //   icon: '/favicon.ico'
              // },
              {
                action: 'dismiss',
                title: '✖️ Dismiss',
                icon: '/favicon.ico'
              }
            ],
            data: {
              bus_id: data.bus_id,
              urgency: urgency,
              notification_id: data.notification_id
            }
          });

          //

        } else {
          // Fallback to regular notification (no actions)
          const notification = new Notification('🚛 Garbage Truck Approaching!', {
            body: `${data.message}\n📍 ${data.distance_miles.toFixed(1)} miles away\n⏱️ ETA: ${data.estimated_arrival} min`,
            icon: '/favicon.ico',
            badge: '/favicon.ico',
            tag: data.notification_id,
            requireInteraction: urgency === 'urgent',
            vibrate: vibrationPatterns[urgency],
            silent: false
          });

          // Handle click for regular notifications
          notification.onclick = () => {
            window.focus();
            setActiveTab('map');
            setSelectedVehicleId(data.bus_id);
            notification.close();
          };

          // Auto-close non-urgent notifications
          if (urgency !== 'urgent') {
            setTimeout(() => notification.close(), 15000);
          }

          //
        }

      } catch (error) {

        // Final fallback - simple notification
        try {
          const simpleNotification = new Notification('🚛 Garbage Truck Approaching!', {
            body: data.message,
            icon: '/favicon.ico'
          });

          simpleNotification.onclick = () => {
            window.focus();
            setActiveTab('map');
            setSelectedVehicleId(data.bus_id);
            simpleNotification.close();
          };

          //
        } catch (fallbackError) {
        }
      }
    } else {
    }

    // Auto-remove from state after time based on urgency
    const removeAfter = urgency === 'urgent' ? 120000 : urgency === 'high' ? 90000 : 60000;
    setTimeout(() => {
      setNotifications(prev => prev.filter(n => n.id !== (data.notification_id || Date.now())));
    }, removeAfter);
  };
  // 2. SOUND NOTIFICATION
  // try {
  //   // Create notification sound
  //   const audio = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+LysGYeCTOG0PLNeSsFJHfH8N2QQAoUXrTp66hVFApGn+LysGYeCTOG0PLNeSsFJHfH8N2QQAoUXrTp66hVFApGn+LysGYeCTOG0PLNeSsFJHfH8N2QQAoUXrTp66hVFA');
  //   audio.volume = 0.7;
  //   await audio.play();
  // } catch (error) {
  //
  // }

  // 2. Permission request function (add this new function)
  const requestNotificationPermissions = async () => {
    const permissions = {
      notifications: false,
      vibration: false
    };

    // Request notification permission
    if ('Notification' in window) {
      if (Notification.permission === 'default') {
        const permission = await Notification.requestPermission();
        permissions.notifications = permission === 'granted';
      } else {
        permissions.notifications = Notification.permission === 'granted';
      }
    }

    // Check vibration support
    if ('navigator' in window && 'vibrate' in navigator) {
      permissions.vibration = true;
    }

    return permissions;
  };

  // 3. Service Worker registration for background notifications (add this)
  const registerServiceWorker = async () => {
    if ('serviceWorker' in navigator) {
      try {
        const registration = await navigator.serviceWorker.register('/sw.js');
        //
        return registration;
      } catch (error) {
      }
    }
  };

  // 4. Initialize permissions and service worker (add to useEffect)
  const initializeNotifications = async () => {
    // Register service worker
    await registerServiceWorker();

    // Request permissions
    const permissions = await requestNotificationPermissions();
    //

    // Show permission status to user
    if (!permissions.notifications) {
      setNotifications(prev => [...prev, {
        id: Date.now(),
        type: 'warning',
        title: 'Enable Notifications',
        message: 'Please enable notifications to receive bus alerts when the app is in the background.',
        timestamp: new Date()
      }]);
    }
  };

  // Handle location updates with timestamp validation to prevent position glitches
  const handleLocationUpdate = (data) => {
    const updateVehicleLocation = (vehicles) => {
      let wasUpdated = false;
      const updated = vehicles.map(vehicle => {
        if (vehicle.id === data.vehicle_id) {
          // Validate timestamp - only update if new data is actually newer
          const currentTimestamp = vehicle.last_updated ? new Date(vehicle.last_updated).getTime() : 0;
          const newTimestamp = data.timestamp ? new Date(data.timestamp).getTime() : Date.now();

          // Skip update if the incoming data is older than current data
          if (newTimestamp < currentTimestamp) {
            return vehicle; // Keep existing position
          }

          wasUpdated = true;
          return {
            ...vehicle,
            last_location: data.location,
            last_updated: data.timestamp
          };
        }
        return vehicle;
      });

      // Only return a new array if something was actually updated to trigger re-render
      return wasUpdated ? updated : vehicles;
    };

    setPublicVehicles(prev => updateVehicleLocation(prev));
    setUserVehicles(prev => updateVehicleLocation(prev));
    setSharedVehicles(prev => updateVehicleLocation(prev));
  };

  // Handle bus approaching notifications
  const handleBusApproaching = (data) => {
    setNotifications(prev => [...prev, {
      id: Date.now(),
      type: 'bus_approaching',
      message: `Bus ${data.route_name} is approaching in ${data.estimated_time} minutes`,
      timestamp: new Date()
    }]);

    // Auto-remove notification after 30 seconds
    setTimeout(() => {
      setNotifications(prev => prev.filter(n => n.id !== Date.now()));
    }, 30000);
  };

  // Advertisement management
  const handleAdView = async (adId) => {
    try {
      await vehicleTrackerService.viewAd(adId);
      //
    } catch (error) {
    }
  };

  const handleAdClick = async (adId, adType = 'unknown') => {
    try {
      await vehicleTrackerService.clickAd(adId, adType);
      //
    } catch (error) {
    }
  };

  // Service Worker message handler function - needs access to hook state
  const handleServiceWorkerMessage = (event) => {
    const { data } = event;

    if (data.type === 'FOCUS_BUS') {
      //

      // Switch to map tab
      setActiveTab('map');

      // Focus on the specific bus
      setSelectedVehicleId(data.bus_id);

      // Center map on the bus if we have location data
      const bus = [...publicVehicles, ...userVehicles, ...sharedVehicles]
        .find(vehicle => vehicle.id === data.bus_id);

      if (bus && bus.last_location) {
        setMapCenter([bus.last_location.lat, bus.last_location.lng]);
        setMapZoom(16);
      }

      // Show confirmation message
      setNotifications(prev => [...prev, {
        id: Date.now(),
        type: 'success',
        title: 'Bus Located',
        message: `Focused on bus from ${data.actionType === 'view' ? 'notification action' : 'notification click'}`,
        timestamp: new Date()
      }]);
    }
  };

  // Initialize everything
  useEffect(() => {
    fetchAllData();
    initializeMapCenter();
    setupWebSocket();

    // Initialize push notifications
    initializePushNotifications();

    // Set up Service Worker message listener
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('message', handleServiceWorkerMessage);
    }

    // Add global error handler for Leaflet positioning errors
    const handleLeafletErrors = (event) => {
      if (event.error && event.error.message && event.error.message.includes('_leaflet_pos')) {
        event.preventDefault(); // Prevent the error from bubbling up
        return true;
      }
    };

    window.addEventListener('error', handleLeafletErrors);

    // Cleanup function - FIXED: Proper WebSocket cleanup
    return () => {
      // Clean up WebSocket connection properly
      if (wsRef.current) {
        //
        wsRef.current.close();
        wsRef.current = null;
      }

      // Also disconnect via manager if it was used
      if (user?.id) {
        try {
          vehicleTrackerService.disconnectWebSocket(user.id);
        } catch (error) {
          // Ignore errors during cleanup
        }
      }

      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.removeEventListener('message', handleServiceWorkerMessage);
      }

      window.removeEventListener('error', handleLeafletErrors);
    };
  }, [user?.id]);

  // Save active tab to localStorage whenever it changes
  useEffect(() => {
    try {
      localStorage.setItem('activeTab', activeTab);
    } catch (error) {
    }
  }, [activeTab]);

  // Update subscribedBusesWithDistance when relevant data changes
  useEffect(() => {
    if (publicVehicles.length > 0 && enhancedSubscriptions.length > 0 && towns.length > 0) {
      const buses = publicVehicles.filter(vehicle => vehicle.vehicle_type === 'bus' || vehicle.vehicle_type === 'truck');
      const filteredBuses = filterSubscribedBuses(buses, enhancedSubscriptions, towns);
      setSubscribedBusesWithDistance(filteredBuses);
    } else {
      setSubscribedBusesWithDistance([]);
    }
  }, [publicVehicles, enhancedSubscriptions, towns]);

  // Periodic refresh for real-time updates
  useEffect(() => {
    let refreshInterval;

    const refreshVehicleData = async () => {
      try {
        // Check if any forms/modals are currently open to avoid disrupting user input
        const hasOpenModals = document.querySelector('.fixed.inset-0, .modal, [role="dialog"]');
        const hasOpenForms = document.querySelector('form input:focus, form textarea:focus, form select:focus');

        if (hasOpenModals || hasOpenForms) {
          return; // Skip refresh to preserve user input
        }

        // Only refresh vehicle location data for real-time updates
        // Use .catch() for user/shared vehicles since they require auth and may fail for non-logged-in users
        const [
          publicVehiclesData,
          userVehiclesData,
          sharedVehiclesData
        ] = await Promise.all([
          vehicleTrackerService.getPublicVehicles(),
          vehicleTrackerService.getUserVehicles().catch(() => []),
          vehicleTrackerService.getSharedVehicles().catch(() => ({ shared_vehicles: [] }))
        ]);

        // Merge vehicle data - keep newer timestamps to avoid overwriting WebSocket updates
        const mergeVehicles = (currentVehicles, newVehicles) => {
          if (!currentVehicles || currentVehicles.length === 0) {
            return newVehicles;
          }

          // Create a map of current vehicles by ID for quick lookup
          const currentMap = new Map(currentVehicles.map(v => [v.id, v]));

          return newVehicles.map(newVehicle => {
            const current = currentMap.get(newVehicle.id);
            if (!current) {
              return newVehicle; // New vehicle, use polling data
            }

            // Compare timestamps - keep whichever has newer data
            const currentTime = current.last_updated ? new Date(current.last_updated).getTime() : 0;
            const newTime = newVehicle.last_updated ? new Date(newVehicle.last_updated).getTime() : 0;

            if (currentTime > newTime) {
              // Current (WebSocket) data is newer, keep it but merge other fields
              return {
                ...newVehicle,
                last_location: current.last_location,
                last_updated: current.last_updated
              };
            }
            return newVehicle; // Polling data is newer
          });
        };

        // Update vehicle data with smart merging
        setPublicVehicles(prev => mergeVehicles(prev, publicVehiclesData));
        setUserVehicles(prev => mergeVehicles(prev, userVehiclesData));
        setSharedVehicles(prev => mergeVehicles(prev, sharedVehiclesData.shared_vehicles || []));

      } catch (error) {
      }
    };

    // Set up periodic refresh every 30 seconds for real-time updates
    refreshInterval = setInterval(refreshVehicleData, 30000);

    return () => {
      if (refreshInterval) {
        clearInterval(refreshInterval);
      }
    };
  }, []);

  // Selective refresh when user returns to the tab
  useEffect(() => {
    let visibilityTimeout;

    const handleVisibilityChange = () => {
      if (!document.hidden && document.visibilityState === 'visible') {
        //

        // Clear any existing timeout
        if (visibilityTimeout) {
          clearTimeout(visibilityTimeout);
        }

        // Debounce the refresh to avoid multiple rapid calls
        visibilityTimeout = setTimeout(async () => {
          try {
            // Check if any forms/modals are currently open to avoid disrupting user input
            const hasOpenModals = document.querySelector('.fixed.inset-0, .modal, [role="dialog"]');
            const hasOpenForms = document.querySelector('form input:focus, form textarea:focus, form select:focus');

            if (hasOpenModals || hasOpenForms) {
              //
              return;
            }

            // Only refresh vehicle location data (most important for real-time tracking)
            // Don't refresh everything to preserve form states and active tabs
            // Use .catch() for user/shared vehicles since they require auth and may fail for non-logged-in users
            const [
              publicVehiclesData,
              userVehiclesData,
              sharedVehiclesData
            ] = await Promise.all([
              vehicleTrackerService.getPublicVehicles(),
              vehicleTrackerService.getUserVehicles().catch(() => []),
              vehicleTrackerService.getSharedVehicles().catch(() => ({ shared_vehicles: [] }))
            ]);

            // Update only the vehicle data without affecting other states
            setPublicVehicles(publicVehiclesData);
            setUserVehicles(userVehiclesData);
            setSharedVehicles(sharedVehiclesData.shared_vehicles || []);

            //
          } catch (error) {
            // Don't show error to user - this is background refresh
          }
        }, 1000); // 1 second debounce
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (visibilityTimeout) {
        clearTimeout(visibilityTimeout);
      }
    };
  }, []);

  const handleFilterChange = (filterId, value) => {
    setMapFilters(prev => ({
      ...prev,
      [filterId]: value
    }));
  };

  // Handle auto-locate
  const handleAutoLocate = async () => {
    try {
      const location = await getCurrentLocation();
      if (location) {
        // Clear any selected vehicle so centering works properly
        setSelectedVehicleId(null);
        setUserLocation(location);
        setMapCenter([location.lat, location.lng]);
        setMapZoom(16);
        setNotifications(prev => [...prev, {
          id: Date.now(),
          type: 'success',
          message: 'Located your position on the map',
          timestamp: new Date()
        }]);
      }
    } catch (error) {
      setNotifications(prev => [...prev, {
        id: Date.now(),
        type: 'error',
        message: 'Unable to get your location. Please enable location services.',
        timestamp: new Date()
      }]);
    }
  };

  // NEW: Separate handler specifically for "Locate Me" button clicks
  const handleLocateMeClick = async () => {
    try {
      const location = await getCurrentLocation();
      if (location) {
        // Clear any selected vehicle to avoid conflicts
        setSelectedVehicleId(null);
        setUserLocation(location);
        setMapCenter([location.lat, location.lng]);
        setMapZoom(16);
        setNotifications(prev => [...prev, {
          id: Date.now(),
          type: 'success',
          message: `Centered map on your location${location.accuracy ? ` (±${Math.round(location.accuracy)}m)` : ''}`,
          timestamp: new Date()
        }]);
      }
    } catch (error) {
      // Use the detailed error message from getCurrentLocation
      setNotifications(prev => [...prev, {
        id: Date.now(),
        type: 'error',
        message: error.message || 'Unable to get your location. Please enable location services.',
        timestamp: new Date()
      }]);
    }
  };

  const testBackgroundNotification = async () => {
    //

    // Test data
    const testData = {
      notification_id: 'test-' + Date.now(),
      message: 'Test bus approaching! This should appear even in background.',
      distance_miles: 1.5,
      estimated_arrival: 3,
      bus_id: 'test-bus-001'
    };

    // Call the notification handler
    await handleBusNotification(testData);
    //

    // Show instructions to user
    setNotifications(prev => [...prev, {
      id: Date.now(),
      type: 'info',
      title: 'Test Sent!',
      message: 'Background notification test sent. Now close/minimize the app to test if it appears as a system notification.',
      timestamp: new Date()
    }]);
  };


  const [pushNotificationStatus, setPushNotificationStatus] = useState({
    supported: false,
    subscribed: false,
    permission: 'default'
  });


  /**
 * Initialize push notifications
 */
  const initializePushNotifications = async () => {
    if (!pushNotificationService.isSupported()) {
      setPushNotificationStatus(prev => ({
        ...prev,
        supported: false
      }));
      return;
    }

    setPushNotificationStatus(prev => ({
      ...prev,
      supported: true,
      permission: Notification.permission
    }));

    // Check if user is already subscribed
    const isSubscribed = await pushNotificationService.getSubscriptionStatus();
    setPushNotificationStatus(prev => ({
      ...prev,
      subscribed: isSubscribed
    }));

    //
  };

  /**
   * Subscribe to push notifications
   */
  const subscribeToPushNotifications = async () => {
    try {
      // Request permission first
      const permissionGranted = await pushNotificationService.requestPermission();

      if (!permissionGranted) {
        setNotifications(prev => [...prev, {
          id: Date.now(),
          type: 'warning',
          title: 'Permission Denied',
          message: 'Push notifications were denied. You can enable them in your browser settings.',
          timestamp: new Date()
        }]);
        return false;
      }

      // Subscribe to push notifications
      const subscribed = await pushNotificationService.subscribe();

      if (subscribed) {
        setPushNotificationStatus(prev => ({
          ...prev,
          subscribed: true,
          permission: 'granted'
        }));

        setNotifications(prev => [...prev, {
          id: Date.now(),
          type: 'success',
          title: 'Push Notifications Enabled',
          message: 'You will now receive bus alerts even when the app is closed!',
          timestamp: new Date()
        }]);

        return true;
      } else {
        setNotifications(prev => [...prev, {
          id: Date.now(),
          type: 'error',
          title: 'Subscription Failed',
          message: 'Failed to enable push notifications. Please try again.',
          timestamp: new Date()
        }]);
        return false;
      }

    } catch (error) {
      setNotifications(prev => [...prev, {
        id: Date.now(),
        type: 'error',
        title: 'Subscription Error',
        message: 'An error occurred while enabling push notifications.',
        timestamp: new Date()
      }]);
      return false;
    }
  };

  /**
   * Unsubscribe from push notifications
   */
  const unsubscribeFromPushNotifications = async () => {
    try {
      const unsubscribed = await pushNotificationService.unsubscribe();

      if (unsubscribed) {
        setPushNotificationStatus(prev => ({
          ...prev,
          subscribed: false
        }));

        setNotifications(prev => [...prev, {
          id: Date.now(),
          type: 'info',
          title: 'Push Notifications Disabled',
          message: 'You will no longer receive background notifications.',
          timestamp: new Date()
        }]);

        return true;
      } else {
        setNotifications(prev => [...prev, {
          id: Date.now(),
          type: 'error',
          title: 'Unsubscribe Failed',
          message: 'Failed to disable push notifications. Please try again.',
          timestamp: new Date()
        }]);
        return false;
      }

    } catch (error) {
      return false;
    }
  };

  /**
   * Send test push notification
   */
  const sendTestPushNotification = async () => {
    try {
      const sent = await pushNotificationService.sendTestNotification();

      if (sent) {
        setNotifications(prev => [...prev, {
          id: Date.now(),
          type: 'success',
          title: 'Test Sent!',
          message: 'Background test notification sent. Close the app to see it appear!',
          timestamp: new Date()
        }]);
        return true;
      } else {
        setNotifications(prev => [...prev, {
          id: Date.now(),
          type: 'error',
          title: 'Test Failed',
          message: 'Failed to send test notification.',
          timestamp: new Date()
        }]);
        return false;
      }
    } catch (error) {
      return false;
    }
  };

  // Custom setActiveTab function that also persists to localStorage
  const handleSetActiveTab = (tab) => {
    setActiveTab(tab);
    try {
      localStorage.setItem('activeTab', tab);
    } catch (error) {
    }
  };

  return {
    // State
    activeTab,
    setActiveTab: handleSetActiveTab,
    publicVehicles,
    userVehicles,
    sharedVehicles,
    busRoutes,
    subscriptions,
    routeSubscriptions,
    enhancedSubscriptions,
    towns,
    ads,
    allPublicAds,
    currentAd,
    setCurrentAd,
    showInterstitial,
    setShowInterstitial,
    currentBannerIndex,
    setCurrentBannerIndex,
    notifications,
    setNotifications,
    loading,
    error,
    selectedVehicleId,
    setSelectedVehicleId,
    mapCenter,
    setMapCenter,
    mapZoom,
    setMapZoom,
    subscribedBusesWithDistance,
    setSubscribedBusesWithDistance,

    // Add these new returns
    mapFilters,
    setMapFilters,
    userLocation,
    setUserLocation,
    handleFilterChange,
    handleAutoLocate,
    handleLocateMeClick, // NEW: Dedicated handler for "Locate Me" button

    // Actions
    fetchAllData,
    initializeMapCenter,
    handleAdView,
    handleAdClick,

    // Setters for external use
    setPublicVehicles,
    setUserVehicles,
    setSharedVehicles,
    setBusRoutes,
    setSubscriptions,
    setRouteSubscriptions,
    setEnhancedSubscriptions,
    setTowns,
    setAds,

    testBackgroundNotification,

    pushNotificationStatus,
    subscribeToPushNotifications,
    unsubscribeFromPushNotifications,
    sendTestPushNotification
  };
};

export default useVehicleTracker;